/**
 * Public Certificate Verification Page
 * Allows anyone to verify a certificate's authenticity without authentication
 * Route: /certificate/:certificateId
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../api/axiosClient';

function PublicCertificateVerification() {
  const { certificateId } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  useEffect(() => {
    if (certificateId) {
      verifyCertificate();
    }
  }, [certificateId]);

  const verifyCertificate = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use public endpoint (no auth required)
      const response = await axios.get(`/public/certificate/${certificateId}`);
      setCertificate(response.data);
    } catch (err) {
      console.error('Error verifying certificate:', err);
      setError(err.response?.data?.detail || 'Certificate not found');
      setCertificate(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: { bg: '#d4edda', border: '#c3e6cb', color: '#155724', text: 'green' },
      REVOKED: { bg: '#f8d7da', border: '#f5c6cb', color: '#721c24', text: 'red' },
      EXPIRED: { bg: '#fff3cd', border: '#ffeaa7', color: '#856404', text: 'orange' }
    };
    return colors[status] || { bg: '#e2e3e5', border: '#d6d8db', color: '#383d41', text: 'gray' };
  };

  const getStatusIcon = (status) => {
    const icons = {
      ACTIVE: 'OK',
      REVOKED: 'X',
      EXPIRED: 'EXP'
    };
    return icons[status] || '?';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          textAlign: 'center',
          background: '#fff',
          padding: '40px',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}>
          <p style={{ color: '#666', fontSize: '18px', margin: 0 }}>
            Verifying certificate...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px'
      }}>
        <div style={{
          maxWidth: '600px',
          background: '#fff',
          padding: '40px',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '60px',
            marginBottom: '20px'
          }}>!</div>
          <h1 style={{ color: '#dc3545', marginBottom: '16px' }}>Certificate Not Found</h1>
          <p style={{ color: '#666', fontSize: '16px', marginBottom: '20px' }}>
            {error}
          </p>
          <p style={{ color: '#999', fontSize: '14px', marginBottom: '24px' }}>
            The certificate you're looking for doesn't exist or may have been removed.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              padding: '12px 28px',
              background: '#007bff',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '16px'
            }}
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (!certificate) {
    return null;
  }

  const statusInfo = getStatusColor(certificate.status);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px'
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto'
      }}>
        {/* Header with status */}
        <div style={{
          background: '#fff',
          borderRadius: '12px 12px 0 0',
          padding: '32px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
          borderBottom: '3px solid #e0e0e0'
        }}>
          {/* Status badge */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '24px'
          }}>
            <div style={{
              background: statusInfo.bg,
              border: `2px solid ${statusInfo.border}`,
              color: statusInfo.color,
              padding: '12px 24px',
              borderRadius: '8px',
              fontWeight: '700',
              fontSize: '18px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '24px' }}>{getStatusIcon(certificate.status)}</span>
              {certificate.status}
            </div>
            <div style={{
              fontSize: '12px',
              color: '#999',
              textAlign: 'right'
            }}>
              Verified on {formatDate(certificate.verified_at)}
            </div>
          </div>

          {/* Title */}
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            margin: '0 0 8px 0',
            color: '#000'
          }}>
            Certificate of {certificate.type.toUpperCase()}
          </h1>
          <p style={{
            fontSize: '14px',
            color: '#666',
            margin: '0',
            marginBottom: '24px'
          }}>
            Certificate ID: {certificate.id} | Version: {certificate.version}
          </p>

          {/* Revocation notice */}
          {certificate.status === 'REVOKED' && (
            <div style={{
              background: '#f8d7da',
              border: '1px solid #f5c6cb',
              color: '#721c24',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <strong>This certificate has been revoked</strong>
              <p style={{ margin: '8px 0 0 0' }}>
                <strong>Revocation Date:</strong> {formatDate(certificate.revoked_at)}
              </p>
              {certificate.revocation_reason && (
                <p style={{ margin: '4px 0 0 0' }}>
                  <strong>Reason:</strong> {certificate.revocation_reason}
                </p>
              )}
            </div>
          )}

          {/* Expiration notice */}
          {certificate.status === 'EXPIRED' && (
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffeaa7',
              color: '#856404',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <strong>This certificate has expired</strong>
              <p style={{ margin: '8px 0 0 0' }}>
                <strong>Expiration Date:</strong> {formatDate(certificate.expiry_date)}
              </p>
            </div>
          )}
        </div>

        {/* Main content */}
        <div style={{
          background: '#fff',
          padding: '32px',
          borderRadius: '0 0 12px 12px',
          boxShadow: '0 5px 20px rgba(0,0,0,0.1)'
        }}>
          {/* Certificate details grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '32px',
            marginBottom: '32px'
          }}>
            {/* Left column */}
            <div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: '#999',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  marginBottom: '4px'
                }}>
                  Certificate Number
                </label>
                <p style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#000',
                  margin: '0',
                  fontFamily: 'monospace'
                }}>
                  {certificate.certificate_number || 'N/A'}
                </p>
              </div>

              {/* Type-specific fields */}
              {certificate.type === 'cpd' && (
                <>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '4px'
                    }}>
                      Course Name
                    </label>
                    <p style={{ fontSize: '16px', color: '#000', margin: '0' }}>
                      {certificate.course_name || 'N/A'}
                    </p>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '4px'
                    }}>
                      Learner Name
                    </label>
                    <p style={{ fontSize: '16px', color: '#000', margin: '0' }}>
                      {certificate.learner_name || certificate.client_name || 'N/A'}
                    </p>
                  </div>
                </>
              )}

              {certificate.type === 'iso' && (
                <>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '4px'
                    }}>
                      Organization Name
                    </label>
                    <p style={{ fontSize: '16px', color: '#000', margin: '0' }}>
                      {certificate.organization_name || 'N/A'}
                    </p>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '4px'
                    }}>
                      ISO Standard
                    </label>
                    <p style={{ fontSize: '16px', color: '#000', margin: '0' }}>
                      {certificate.iso_standard || 'N/A'}
                    </p>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '4px'
                    }}>
                      Scope
                    </label>
                    <p style={{ fontSize: '16px', color: '#000', margin: '0' }}>
                      {certificate.scope || 'N/A'}
                    </p>
                  </div>
                </>
              )}

              {certificate.type === 'company_assessment' && (
                <>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '4px'
                    }}>
                      Company Name
                    </label>
                    <p style={{ fontSize: '16px', color: '#000', margin: '0' }}>
                      {certificate.company_name || 'N/A'}
                    </p>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '4px'
                    }}>
                      Assessment Category
                    </label>
                    <p style={{ fontSize: '16px', color: '#000', margin: '0' }}>
                      {certificate.assessment_category || 'N/A'}
                    </p>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '4px'
                    }}>
                      Level Achieved
                    </label>
                    <p style={{ fontSize: '16px', color: '#000', margin: '0' }}>
                      {certificate.level_achieved || 'N/A'}
                    </p>
                  </div>
                </>
              )}

              {certificate.type === 'pcaf' && (
                <>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '4px'
                    }}>
                      Learner Name
                    </label>
                    <p style={{ fontSize: '16px', color: '#000', margin: '0' }}>
                      {certificate.learner_name || 'N/A'}
                    </p>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '4px'
                    }}>
                      Competency Type
                    </label>
                    <p style={{ fontSize: '16px', color: '#000', margin: '0' }}>
                      {certificate.competency_type || 'N/A'}
                    </p>
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      color: '#999',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'block',
                      marginBottom: '4px'
                    }}>
                      CPD Hours
                    </label>
                    <p style={{ fontSize: '16px', color: '#000', margin: '0' }}>
                      {certificate.cpd_hours || 'N/A'}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Right column */}
            <div>
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: '#999',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  marginBottom: '4px'
                }}>
                  Issue Date
                </label>
                <p style={{ fontSize: '16px', color: '#000', margin: '0' }}>
                  {formatDate(certificate.issue_date)}
                </p>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: '#999',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display: 'block',
                  marginBottom: '4px'
                }}>
                  Expiry Date
                </label>
                <p style={{ fontSize: '16px', color: '#000', margin: '0' }}>
                  {formatDate(certificate.expiry_date)}
                </p>
              </div>

              {certificate.cert_body && (
                <div style={{ marginBottom: '24px' }}>
                  <label style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    color: '#999',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    display: 'block',
                    marginBottom: '4px'
                  }}>
                    Issuing Body
                  </label>
                  <p style={{ fontSize: '16px', color: '#000', margin: '0' }}>
                    {certificate.cert_body}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* QR Code Section */}
          <div style={{
            borderTop: '1px solid #e0e0e0',
            paddingTop: '32px',
            marginBottom: '32px'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '700',
              color: '#000',
              marginBottom: '16px'
            }}>
              QR Code Verification
            </h3>
            <div style={{
              background: '#f9f9f9',
              padding: '20px',
              borderRadius: '8px',
              textAlign: 'center',
              border: '1px solid #e0e0e0'
            }}>
              <p style={{ color: '#666', marginBottom: '12px' }}>
                Scan this QR code to verify the certificate:
              </p>
              <p style={{
                fontSize: '12px',
                color: '#999',
                fontFamily: 'monospace',
                margin: '0'
              }}>
                {certificate.verification_url}
              </p>
            </div>
          </div>

          {/* Version history */}
          {certificate.version_summary && certificate.version_summary.length > 0 && (
            <div style={{
              borderTop: '1px solid #e0e0e0',
              paddingTop: '32px'
            }}>
              <button
                onClick={() => setShowVersionHistory(!showVersionHistory)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#007bff',
                  padding: '0',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span>{showVersionHistory ? 'Hide' : 'Show'}</span>
                Version History ({certificate.version_summary.length})
              </button>

              {showVersionHistory && (
                <div style={{
                  background: '#f9f9f9',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0'
                }}>
                  {certificate.version_summary.map((version, index) => (
                    <div key={index} style={{
                      paddingBottom: '12px',
                      borderBottom: index < certificate.version_summary.length - 1 ? '1px solid #e0e0e0' : 'none',
                      marginBottom: '12px'
                    }}>
                      <p style={{
                        margin: '0 0 4px 0',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#000'
                      }}>
                        v{version.version} - {version.action}
                      </p>
                      <p style={{
                        margin: '0',
                        fontSize: '12px',
                        color: '#999'
                      }}>
                        {formatDate(version.timestamp)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: '40px',
          color: '#fff'
        }}>
          <p style={{ fontSize: '13px', margin: '0' }}>
            This certificate is verified and authenticated by the Council for Standards
          </p>
        </div>
      </div>
    </div>
  );
}

export default PublicCertificateVerification;
